<template>
  <table class="previewSection">
    <tbody>
      <tr>
        <td class="previewSection__td">
          <div
            @mouseover="mouseOverSection"
            @mouseleave="mouseLeaveSection"
          >
            <!-- セクション リサイズ -->
            <vue-draggable-resizable
              class="section"
              :class="sectionCssClass(section.name)"
              :style="section.styles"
              :h="height"
              :min-height="minHeight"
              :max-height="maxHeight"
              :handles="['br']"
              :draggable="false"
              :enable-native-drag="true"
              :enable-show-handle-mouseover="true"
              :id="'section' + section.id"
              :onResizeStart="resizeStart"
              @resizing="onResize"
              @resizestop="onResizeStop"
              @activated="setHeightRange"
              :ref="'resize' + section.id"
            >
              <table
                :style="draggingPartsContainerStyle(section.id)"
                @dragover="partsContainerDragOver(section.id)"
              >
                <div
                  v-if="$store.state.status.showDragPartsContainerAssist"
                  :style="draggingPartsContainerHeaderStyle(section.id)"
                >
                  {{ section.name }}
                </div>
                <tbody>
                  <tr
                    is="draggable"
                    v-model="partsContainers"
                    :group="{ name: 'section', put: ['section', 'contentParts'] }"
                    :style="sectionInnerStyle"
                    handle=".partsWindow__left__move"
                    @change="onChange($event)"
                    @start="onPartsContainerDragStart(section.parts_containers.length)"
                    @choose="isChoose = true"
                    @unchoose="isChoose = false"
                    @end="onPartsContainerDragEnd"
                  >
                    <td
                      is="preview-parts-container"
                      v-for="partsContainer in partsContainers"
                      :key="partsContainer.id"
                      :partsContainer="partsContainer"
                      :sectionId="section.id"
                      :ref="getRefsName(partsContainer.id)"
                      :columnCount="partsContainer.column_count"
                      @updateSectionHeight="changeWindowHeight()"
                      @toggleSectionHoverState="partsHoverState"
                      :dragging.sync="draggingSectionPadding"
                      :isChoose="isChoose"
                    />
                  </tr>
                  <tr
                    is="draggable"
                    v-if="!section.parts_containers.length"
                    v-model="partsContainers"
                    :group="{ name: 'section', put: true }"
                    @change="onChange($event)"
                    @start="onPartsContainerDragStart()"
                    @end="onPartsContainerDragEnd"
                  >
                    <div class="partsContainer partsContainer--dummy" :ref="'dummy'+section.id">
                      Drop Parts Block Here!!
                    </div>
                  </tr>
                </tbody>
              </table>
              <!-- セクションにマウスオーバーで出てくる領域 -->
              <transition name="section">
                <hover-button v-if="active && !draggingSectionPadding && !partsHover || showColorPicker" :section="section"/>
              </transition>
              <!-- 背景色のカラーピッカー -->
              <color-picker
                v-on-clickaway="colorPickerAway"
                v-if="showColorPicker"
                v-model="color"
                class="background-color-picker"
                :disableAlpha="true"
              />
              <!-- セクションリサイズ中に重ねる領域 -->
              <div v-if="resizing" :style="sectionResizingTop">
                <div class="section-resize-paddingtop">
                  {{ sectionPaddingTop }} px
                </div>
              </div>
              <div v-if="resizing" :style="sectionResizingBottom">
                <div class="section-resize-paddingbottom">
                  {{ sectionPaddingBottom }} px
                </div>
              </div>
            </vue-draggable-resizable>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import Draggable from 'vuedraggable'
import VueDraggableResizable from '../../../../public/vendor/vue-draggable-resizable/dist/VueDraggableResizable.umd.min.js'
import '../../../../public/vendor/vue-draggable-resizable/dist/VueDraggableResizable.css'
import { Sketch } from 'vue-color'
import HoverButton from '@/components/Content/Preview/PreviewSection/PreviewSectionHoverButton'
import PreviewPartsContainer from '@/components/Content/Preview/PreviewPartsContainer'

import { mixin as onClickAway } from 'vue-clickaway'

const SECTION_PADDING_DEFAULT = 0
const SECTION_PADDING_MAX = 200
const SECTION_RESIZE_OVERLAY_STYLE = {
  backgroundColor: 'rgba(35,24,21,0.2)',
  position: 'absolute',
  pointerEvents: 'none',
  left: 0,
  right: 0,
  borderTop: '1px solid rgba(35,24,21,0.75)',
  borderBottom: '1px solid rgba(35,24,21,0.75)',
}
const SECTION_RESIZE_OVERLAY_STYLE_BORDER_HEIGHT = 2

export default {
  name: 'PreviewSection',
  components: {
    Draggable,
    VueDraggableResizable,
    PreviewPartsContainer,
    HoverButton,
    'ColorPicker': Sketch
  },
  mixins: [onClickAway],
  data () {
    return {
      width: 10,
      height: 10, // セクションの高さ初期値(0だとrefsがとれない)
      minHeight: 10, // セクションの最小高さ：セクションがアクティブになった時にセットする
      maxHeight: 10, // セクションの最大高さ：セクションがアクティブになった時にセットする
      resizeSection: '',
      resizeSectionHeight: 0, // リサイズ中に一時的にセクション高さを保存
      draggingSectionPadding: false,
      resizing: false,
      active: false,
      partsHover: false,
      isChoose: false,
      color: { hex: this.sectionBackgroundColor }, // カラーピッカーに渡す色コード
    }
  },
  props: ['section'],
  async mounted () {
    await this.waitImages()
    this.changeWindowHeight()
    window.addEventListener('resize', this.changeWindowWidth)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.changeWindowWidth)
  },
  methods: {
    async waitImages () {
      // safariでは、画像ロードを待たないと 高さ計算イベントが空振りしてしまうので対策
      const images = this.$refs['resize' + this.section.id].$el.getElementsByTagName('img')
      // 画像を待つように
      const promises = []
      const loadImage = img => {
        return new Promise((resolve, reject) => {
          if (img.complete || !img.src) resolve(img)
          img.addEventListener('load', () => resolve(img))
          img.addEventListener('error', () => resolve(img))
        })
      }
      images.forEach(image => promises.push(loadImage(image)))
      return Promise.all(promises)
    },
    // ウィンドウ幅変更時対策
    changeWindowWidth () {
      this.changeSectionHeight()
    },
    // 描画後高さを再計算
    changeWindowHeight () {
      this.$nextTick(() => {
        this.changeSectionHeight()
      })
    },
    // セクションの高さ変更
    changeSectionHeight (draggingSectionId = null) {
      if (draggingSectionId && this.$store.state.status.dragPartsContainerActiveSection === draggingSectionId) {
        this.height = this.sectionHeight + this.sectionBorderWidth + this.$store.getters.noUnitScssVars.dragPartsContainerHeight
      } else {
        this.height = this.sectionHeight + this.sectionBorderWidth
      }
    },
    onPartsContainerDragStart (partsContainersCount = null) {
      if (partsContainersCount && partsContainersCount === 1) {
        this.$store.commit('showErrorMessagePopup', ['セクションにパーツは最低1つ必要です'])
        return
      }
      this.$store.commit('toggleDragPartsContainerAssist', { showState: true })
      this.draggingSectionPadding = true
    },
    onPartsContainerDragEnd () {
      this.$store.commit('toggleDragPartsContainerAssist', { showState: false })
      this.changeSectionHeight()
      this.draggingSectionPadding = false
      // order_idの番号を並び替えしてcommit
      this.arrangePartsContainer()
    },
    // リサイズするセクションを決定
    resizeStart () {
      this.resizing = true
      this.draggingSectionPadding = true
      // リサイズ可能範囲を更新
      this.setHeightRange()
      this.resizeSection = this.index
      // リサイズするセクションの高さを一時保管
      this.resizeSectionHeight = this.partsHeight + this.sectionTotalInnerPadding
    },
    // リサイズ後の高さを取得
    onResize (left, top, width, height) {
      this.height = height
      // padding処理(表示用数値)
      let padding = (height - this.resizeSectionHeight - this.sectionBorderWidth) / 2
      this.sectionPaddingTop = padding
      this.sectionPaddingBottom = padding
    },
    // リサイズ完了後、高さを反映させる
    onResizeStop (left, top, width, height) {
      this.resizing = false
      this.draggingSectionPadding = false
      this.height = height
      // 一時保管していたセクションの高さをリセット
      this.resizeSectionHeight = 0
    },
    // ドラッグでのパーツ移動処理
    onChange (event) {
      // パーツをドラッグされたセクションの高さを再計算
      this.changeSectionHeight()
      if (event.added) {
        // 新規パーツがドラッグで追加された場合：order_idの番号の並び替えを行い、パレットを開く
        const addPart = event.added.element
        if (!addPart.order_id) {
          this.arrangePartsContainer(addPart)
          this.$store.commit('updateStatusPalette', {
            type: addPart.type,
            sectionId: this.section.id,
            partsContainerId: addPart.id
          })
        }
      }
    },
    // セクションの最小高さをセット
    setHeightRange () {
      const sectionMinHeight = this.partsHeight + this.sectionBorderWidth + this.sectionTotalInnerPadding
      const sectionMaxHeight = this.sectionMaxHeight + this.sectionBorderWidth + this.sectionTotalInnerPadding
      // トータルの高さ
      this.minHeight = sectionMinHeight
      this.maxHeight = sectionMaxHeight

      // パーツの複製時にvue-draggable-risizableのonChangeが発火しないため、直接domの最小最大高さを変更する。
      this.$refs['resize' + this.section.id].minH = sectionMinHeight
      this.$refs['resize' + this.section.id].maxH = sectionMaxHeight
    },
    // previewで使うrefs名を決める
    getRefsName (partsContainerId) {
      const refsName = 'partsContainer' + Number(partsContainerId)
      return refsName
    },
    // セクションのマウスオーバー
    mouseOverSection () {
      this.active = true
    },
    // セクションのマウスを離した時の処理
    mouseLeaveSection () {
      this.active = false
    },
    // パーツホバー時のセクションホバーの表示切り替え
    partsHoverState (state) {
      this.partsHover = state
    },
    // パーツの並び替えと、セクションIDのセット
    arrangePartsContainer (addPartsContainer) {
      this.$store.commit('sortPartsContainers', {
        addPartsContainer,
        sectionId: this.section.id
      })
    },
    // セクションの padding をセット
    setSectionPadding (value, prop) {
      this.$store.commit('updateSectionStyles', {
        sectionName: this.section.name,
        styles: [
          { prop, value: value + 'px' }
        ]
      })
    },
    colorPickerAway () {
      if (this.showColorPicker) {
        this.$store.commit('updateStatusColorPicker', { prop: 'sectionId', value: null })
      }
    },
    // 罫線のスタイルから、太さのみを取得
    getBorderWidth (borderStyle) {
      // スタイルが solid 2px #000000 の形で渡されるので、太さの部分だけ取り出す
      // 何も設定されていなかったら、0px とする
      const borderWidth = borderStyle ? Number(borderStyle.split(' ')[1].replace(/px/g, '')) : 0
      return borderWidth
    },
    // 余白を取得
    getPadding (padding) {
      return padding ? Number(padding.replace(/px/g, '')) : SECTION_PADDING_DEFAULT
    },
    // 余白の数値に、 px 単位を付与：未設定の場合は、既定値を返す
    toPaddingPixel (padding) {
      return padding ? padding + 'px' : SECTION_PADDING_DEFAULT + 'px'
    },
    partsContainerDragOver (sectionId) {
      this.$store.commit('setDragPartsContainerActiveSection', { sectionId: sectionId })
      this.changeSectionHeight(sectionId)
    },
  },
  computed: {
    dragPartsContainerActiveSection () {
      return this.$store.state.status.dragPartsContainerActiveSection
    },
    // パーツコンテナドラッグ中のセクション
    draggingPartsContainerStyle: () => {
      return function (sectionId) {
        if (this.$store.state.status.showDragPartsContainerAssist) {
          if (this.$store.state.status.dragPartsContainerActiveSection === sectionId) {
            return {
              'outline': '2px solid #5496E8',
              'outline-offset': '-2px',
            }
          } else {
            return {
              'outline': '2px solid rgba(35,24,21,0.75)',
              'outline-offset': '-2px',
            }
          }
        } else {
          return {}
        }
      }
    },
    draggingPartsContainerHeaderStyle: () => {
      return function (sectionId) {
        if (this.$store.state.status.showDragPartsContainerAssist) {
          if (this.$store.state.status.dragPartsContainerActiveSection === sectionId) {
            return {
              'position': 'absolute',
              'margin-top': '-16px',
              'width': '60px',
              'height': '16px',
              'font-size': '12px',
              'text-align': 'center',
              'color': 'white',
              'background-color': '#5496E8',
            }
          } else {
            return {
              'position': 'absolute',
              'margin-top': '-16px',
              'width': '60px',
              'height': '16px',
              'font-size': '12px',
              'text-align': 'center',
              'color': 'white',
              'background-color': 'rgba(35,24,21,0.75)',
            }
          }
        } else {
          return {}
        }
      }
    },
    // セクションのカラーピッカーを表示するかどうか
    showColorPicker () {
      // カラーピッカーを表示しているセクションと、セクションIDが同じ場合：表示
      return (this.$store.getters.getStatusColorPicker.sectionId === this.section.id)
    },
    partsContainers: {
      cache: false,
      get () {
        return this.section.parts_containers
      },
      set (value) {
        this.$store.commit('updateSectionPartsContainers', {
          value,
          sectionId: this.section.id
        })
      }
    },
    ContentStyles: {
      cache: false,
      get () {
        return this.$store.getters.findContentStyles
      }
    },
    // セクションの高さ
    partsHeight: {
      cache: false,
      get () {
        // トータルの高さ
        let totalHeight = 0
        this.section.parts_containers.forEach(partsContainer => {
          // previewで使うrefs名を決める
          const refsName = this.getRefsName(partsContainer.id)

          // パーツの高さ
          const ref = this.$refs[refsName][0]
          if (!ref) return
          const partsContainerHeight = ref.$el.clientHeight

          // パーツの高さを、トータルの高さに加える
          totalHeight += partsContainerHeight
        })
        // ダミーパーツの高さを、トータルの高さに加える
        totalHeight += this.dummyHeight
        return totalHeight
      }
    },
    sectionCssClass: function () {
      return function (sectionName) {
        // section-header の形
        return `section-${sectionName}`
      }
    },
    sectionBackgroundColor: {
      cache: false,
      get () {
        return this.$store.getters.findSectionStyle(this.section.name, 'background')
      }
    },
    sectionPaddingTop: {
      cache: false,
      get () {
        const padding = this.$store.getters.findSectionStyle(this.section.name, 'padding-top')
        return this.getPadding(padding)
      },
      set (value) {
        this.setSectionPadding(value, 'padding-top')
      }
    },
    sectionPaddingBottom: {
      cache: false,
      get () {
        const padding = this.$store.getters.findSectionStyle(this.section.name, 'padding-bottom')
        return this.getPadding(padding)
      },
      set (value) {
        this.setSectionPadding(value, 'padding-bottom')
      }
    },
    // セクションの罫線太さ
    sectionBorderWidth: {
      cache: false,
      get () {
        const borderTop = this.sectionBorderTop
        const borderBottom = this.sectionBorderBottom
        const innerBorderTop = this.sectionInnerBorderTop
        const innerBorderBottom = this.sectionInnerBorderBottom

        // スタイルが solid 2px #000000 の形で取得されるので、太さの部分だけ取り出す
        // 何も設定されていなかったら、0px とする
        const borderTopWidth = this.getBorderWidth(borderTop)
        const borderBottomWidth = this.getBorderWidth(borderBottom)
        const innerBorderTopWidth = this.getBorderWidth(innerBorderTop)
        const innerBorderBottomWidth = this.getBorderWidth(innerBorderBottom)

        const borderTotalWidth = borderTopWidth + borderBottomWidth + innerBorderTopWidth + innerBorderBottomWidth
        return borderTotalWidth
      }
    },
    sectionBorderTop: {
      cache: false,
      get () {
        return this.$store.getters.findSectionStyle(this.section.name, 'border-top')
      }
    },
    sectionBorderBottom: {
      cache: false,
      get () {
        return this.$store.getters.findSectionStyle(this.section.name, 'border-bottom')
      }
    },
    sectionInnerBackground: {
      cache: false,
      get () {
        return this.$store.getters.findSectionStyle(this.section.name, 'inner_background')
      }
    },
    sectionInnerBorderTop: {
      cache: false,
      get () {
        return this.$store.getters.findSectionStyle(this.section.name, 'inner_border-top')
      }
    },
    sectionInnerBorderBottom: {
      cache: false,
      get () {
        return this.$store.getters.findSectionStyle(this.section.name, 'inner_border-bottom')
      }
    },
    sectionInnerPaddingTop: {
      cache: false,
      get () {
        const padding = this.$store.getters.findSectionStyle(this.section.name, 'inner_padding-top')
        return this.getPadding(padding)
      }
    },
    sectionInnerPaddingBottom: {
      cache: false,
      get () {
        const padding = this.$store.getters.findSectionStyle(this.section.name, 'inner_padding-bottom')
        return this.getPadding(padding)
      }
    },
    // セクション外側の余白合計
    sectionTotalPadding () {
      return this.sectionPaddingTop + this.sectionPaddingBottom
    },
    // セクション内側の余白合計
    sectionTotalInnerPadding () {
      return this.sectionInnerPaddingTop + this.sectionInnerPaddingBottom
    },
    sectionHeight: {
      cache: false,
      get () {
        // padding の高さを追加(上下)
        return this.partsHeight + this.sectionTotalPadding + this.sectionTotalInnerPadding
      }
    },
    sectionMaxHeight: {
      cache: false,
      get () {
        // padding の最大高さを追加(上下)
        return this.partsHeight + SECTION_PADDING_MAX * 2
      }
    },
    dummyHeight: {
      cache: false,
      get () {
        let dummyHeight = 0
        // previewで使う、ダミーパーツのrefs名を決める
        let refsName = 'dummy' + this.section.id
        // ダミーパーツがある場合、ダミーパーツの高さを取得
        if (this.$refs[refsName]) {
          dummyHeight = this.$refs[refsName].clientHeight
        }
        return dummyHeight
      }
    },
    sectionInnerStyle: {
      cache: false,
      get () {
        return {
          'background-color': this.sectionInnerBackground,
          'border-top': this.sectionInnerBorderTop,
          'border-bottom': this.sectionInnerBorderBottom,
          'padding-top': this.toPaddingPixel(this.sectionInnerPaddingTop),
          'padding-bottom': this.toPaddingPixel(this.sectionInnerPaddingBottom),
        }
      }
    },
    // リサイズ中に overlay する範囲
    sectionResizingTop () {
      let height = this.sectionPaddingTop - SECTION_RESIZE_OVERLAY_STYLE_BORDER_HEIGHT
      if (height < 0) height = 0
      return {
        ...SECTION_RESIZE_OVERLAY_STYLE,
        'top': 0,
        'height': `${height}px`
      }
    },
    sectionResizingBottom () {
      let height = this.sectionPaddingBottom - SECTION_RESIZE_OVERLAY_STYLE_BORDER_HEIGHT
      if (height < 0) height = 0
      return {
        ...SECTION_RESIZE_OVERLAY_STYLE,
        bottom: 0,
        height: `${height}px`
      }
    }
  },
  watch: {
    'color.hex8' (value) {
      // 透明が選択された場合、色を空欄にする：transparent入れると、黒が選択されてしまうため
      if (value === '#00000000') {
        this.color = { hex: '' }
      }
    },
    'color.hex' (value) {
      this.$store.commit('updateSectionStyles', {
        sectionName: this.section.name,
        styles: [
          { prop: 'background', value, }
        ]
      })
    },
    'section.styles': {
      deep: true,
      handler: function () {
        this.changeWindowHeight()
      }
    },
    'section.parts_containers': {
      deep: true,
      handler: function () {
        this.$nextTick(async () => {
          await this.waitImages()
          this.changeWindowHeight()
        })
      }
    },
    ContentStyles: {
      deep: true,
      handler: function () {
        this.changeWindowHeight()
      }
    },
    sectionBackgroundColor (value) {
      this.color = { hex: value }
    },
    sectionPaddingTop () {
      this.changeSectionHeight()
    },
    sectionPaddingBottom () {
      this.changeSectionHeight()
    },
    sectionTotalInnerPadding () {
      this.changeSectionHeight()
    },
    dragPartsContainerActiveSection () {
      this.changeSectionHeight(this.section.id)
    },
  }
}
</script>

<style lang="scss" scoped>
.previewSection {
  width: 100%;
  margin: 0 auto;
  align: 'center';
  &__td {
    padding: 0;
  }
  .vdr {
    /* vue-draggable-resizable の border を消す */
    border: none;
  }
}

// 各セクション
.section {
  table {
    margin: 0 auto;
  }
}

/* セクションにマウスホバーで表示される枠：表示/非表示切り替え時のトランジション */
.section-enter-active, .section-leave-active {
  transition: opacity .5s;
}

.section-enter, .section-leave-to {
  opacity: 0;
}

/* セクションのリサイズ中に表示する padding の値 */
.section-resize {
  position: absolute;
  right: 0;
  z-index: 10;
  // 以下、設定しないとセクションのスタイル設定が反映されてしまう
  color: #fff;
  font-size: 1rem;
  line-height: normal;
  font-family: -apple-system,Segoe UI,Helvetica Neue,Hiragino Kaku Gothic ProN,"メイリオ",meiryo,sans-serif;
}

.section-resize-paddingtop {
  @extend .section-resize;
  bottom: 0;
}

.section-resize-paddingbottom {
  @extend .section-resize;
  top: 0;
}

</style>

<style lang="scss">
/* セクションリサイズ時に表示されるハンドル */
.handle {
  z-index: 1;
  bottom: -1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(35,24,21,0.4);
  cursor: pointer;
  text-align: center;
  line-height: 40px;
  color: #fff;
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border: 0px;
    border-right: solid 2px #fff;
    border-bottom: solid 2px #fff;
    -ms-transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    transform: rotate(45deg);
    position: absolute;
    top: 80%;
    left: calc(50% - 3px);
    margin-top: -4px;
  }
  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border: 0px;
    border-left: solid 2px #fff;
    border-top: solid 2px #fff;
    -ms-transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    transform: rotate(45deg);
    position: absolute;
    top: 30%;
    left: calc(50% - 3px);
    margin-top: -4px;
  }
}

/* 背景色のカラーピッカー */
div .background-color-picker {
  top: 80px;
  position: absolute;
  z-index: 2;
}

/* https://github.com/SortableJS/Sortable/issues/663#issuecomment-166718414 */
.section {
  .sortable-chosen, sortable-chosen.sortable-ghost {
    opacity: 1;
  }
  .sortable-ghost {
    background-color: rgba(84, 150, 232, 0.7);
    padding: 10px;
    text-align: center;
    opacity: 1;
    .ghost-text {
      color: #fff;
    }
  }
}

</style>
